/**
 * Tauri integration shim — runtime-detected, lazy-loaded.
 *
 * Web build never references @tauri-apps/api at module load time. Only when
 * running INSIDE the Tauri shell (window.__TAURI_INTERNALS__ injected) does
 * `onTauriFilesDropped` / `onTauriDragState` dynamically import the package.
 * Vite emits the import as a separate chunk that the browser never fetches —
 * the chunk file ships to Vercel but no user agent loads it.
 *
 * Pairs with apps/desktop/src-tauri/src/lib.rs which emits:
 *   - "collect:files-dropped" → { files, skipped }
 *   - "collect:drag-state"    → { state, count }
 */

export interface TauriDroppedFile {
  name: string;
  path: string;
  size: number;
  mime: string;
  content_b64: string;
}

export interface TauriDropPayload {
  files: TauriDroppedFile[];
  skipped: { name: string; reason: string }[];
}

export interface TauriDragState {
  state: 'enter' | 'leave' | 'drop';
  count: number;
}

export type Unlisten = () => void;

/** True only when running inside the Tauri shell. */
export function isTauri(): boolean {
  if (typeof window === 'undefined') return false;
  return typeof (window as unknown as { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__ !== 'undefined';
}

/**
 * Subscribe to native file-drop events from Tauri. Returns a no-op unlisten
 * when running in a plain browser, so callers can register unconditionally.
 */
export async function onTauriFilesDropped(
  cb: (payload: TauriDropPayload) => void,
): Promise<Unlisten> {
  if (!isTauri()) return () => {};
  const { listen } = await import('@tauri-apps/api/event');
  const off = await listen<TauriDropPayload>('collect:files-dropped', (e) => cb(e.payload));
  return off;
}

export async function onTauriDragState(
  cb: (state: TauriDragState) => void,
): Promise<Unlisten> {
  if (!isTauri()) return () => {};
  const { listen } = await import('@tauri-apps/api/event');
  const off = await listen<TauriDragState>('collect:drag-state', (e) => cb(e.payload));
  return off;
}

/**
 * Convert the Rust-side payload into browser File objects so it can be fed
 * into the existing addFiles() flow in SendCapsule. File extends Blob, so
 * .text() decodes UTF-8 and .arrayBuffer() / .stream() also work.
 */
export function tauriDropToFiles(payload: TauriDropPayload): File[] {
  return payload.files.map((f) => {
    const bin = atob(f.content_b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], f.name, { type: f.mime });
  });
}


// ─── Auto-updater ──────────────────────────────────────────────────────────

export interface UpdateInfo {
  version: string;
  currentVersion: string;
  date?: string;
  body?: string;
}

/**
 * Check the configured updater endpoint for a newer version. Returns null
 * when running in a plain browser, or when no update is available, or on
 * any error (silent — the dashboard shouldn't nag if the endpoint is down).
 */
export async function checkForUpdate(): Promise<UpdateInfo | null> {
  if (!isTauri()) return null;
  try {
    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check();
    if (!update) return null;
    return {
      version: update.version,
      currentVersion: update.currentVersion,
      date: update.date,
      body: update.body,
    };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.warn('[updater] check failed:', e?.message || e);
    return null;
  }
}

// ─── Python sidecar ────────────────────────────────────────────────────────

export interface RunResult {
  stdout: string;
  stderr: string;
  code: number | null;
  cmd: string;
}

export interface PythonCheck {
  ok: boolean;
  version: string;
  path: string;
  error?: string | null;
}

/**
 * Spawn the Collect CLI from the Tauri shell and wait for it to exit.
 * Web build always rejects (browser can't spawn processes).
 */
export async function runCollect(
  pythonPath: string,
  cliPath: string,
  args: string[],
  cwd?: string,
): Promise<RunResult> {
  if (!isTauri()) throw new Error('runCollect only works inside the Tauri shell');
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke<RunResult>('run_collect', { pythonPath, cliPath, args, cwd });
}

/** Verify a Python interpreter path responds to `--version`. */
export async function checkPython(pythonPath: string): Promise<PythonCheck> {
  if (!isTauri()) throw new Error('checkPython only works inside the Tauri shell');
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke<PythonCheck>('check_python', { pythonPath });
}


/**
 * Download and install the update, then relaunch the app. Returns false on
 * web. Throws on error so the caller can surface it.
 */
export async function applyUpdate(onProgress?: (downloaded: number, total: number) => void): Promise<boolean> {
  if (!isTauri()) return false;
  const { check } = await import('@tauri-apps/plugin-updater');
  const { relaunch } = await import('@tauri-apps/plugin-process');
  const update = await check();
  if (!update) return false;
  let total = 0;
  let downloaded = 0;
  await update.downloadAndInstall((event) => {
    if (event.event === 'Started') {
      total = event.data.contentLength || 0;
      onProgress?.(0, total);
    } else if (event.event === 'Progress') {
      downloaded += event.data.chunkLength;
      onProgress?.(downloaded, total);
    } else if (event.event === 'Finished') {
      onProgress?.(total, total);
    }
  });
  await relaunch();
  return true;
}
